<template>
  <div class="app-container">
    <h1>Inwestycje deweloperskie</h1>

    <div class="filter-container">
      <el-input
        v-model="query.search"
        placeholder="Nazwa / deweloper / lokalizacja"
        style="width: 260px;"
        class="filter-item"
        clearable
        @clear="handleFilter"
        @keyup.enter.native="handleFilter"
      />
      <el-select
        v-model="query.status"
        class="filter-item"
        placeholder="Status: wszystkie"
        clearable
        @change="handleFilter"
      >
        <el-option label="Czeka na akceptację" value="in_active" />
        <el-option label="Aktywna" value="active" />
        <el-option label="Odrzucona" value="rejected" />
        <el-option label="Wyłączona przez użytkownika" value="in_active_by_user" />
      </el-select>
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
        Szukaj
      </el-button>
    </div>

    <el-table v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%">
      <el-table-column align="center" label="Zdjęcie" width="90">
        <template slot-scope="scope">
          <img
            v-if="scope.row.main_photo && scope.row.main_photo.url"
            :src="scope.row.main_photo.url"
            style="width: 64px; height: 48px; object-fit: cover; border-radius: 4px;"
          >
          <span v-else>—</span>
        </template>
      </el-table-column>

      <el-table-column label="Nazwa">
        <template slot-scope="scope">
          <a :href="frontendUrl + '/inwestycja/' + scope.row.slug" target="_blank" class="link-type">
            {{ scope.row.name }}
          </a>
          <div class="sub-text">{{ scope.row.location_name }}</div>
        </template>
      </el-table-column>

      <el-table-column label="Deweloper">
        <template slot-scope="scope">
          <span>{{ scope.row.developer_name || '—' }}</span>
          <div class="sub-text">{{ scope.row.user_email }}</div>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Lokale" width="150">
        <template slot-scope="scope">
          <div>{{ scope.row.active_units_count }} / {{ scope.row.units_count }} aktywnych</div>
          <el-tag v-if="scope.row.pending_units_count > 0" type="warning" size="mini">
            {{ scope.row.pending_units_count }} czeka na akceptację
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Status" width="220">
        <template slot-scope="scope">
          <el-select
            v-model="scope.row.status"
            @change="changeStatus(scope.row, scope.row.status)"
          >
            <el-option label="Czeka na akceptację" value="in_active" />
            <el-option label="Aktywna" value="active" />
            <el-option label="Odrzucona" value="rejected" />
            <el-option label="Wyłączona przez użytkownika" value="in_active_by_user" />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Dodano" width="110">
        <template slot-scope="scope">
          <span>{{ formatDate(scope.row.created_at) }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Akcja" width="230">
        <template slot-scope="scope">
          <el-button
            v-permission="['manage offer']"
            type="success"
            size="mini"
            icon="el-icon-check"
            :disabled="scope.row.pending_units_count === 0"
            :loading="activatingUnitsId === scope.row.id"
            @click="activateUnits(scope.row)"
          >
            Aktywuj lokale ({{ scope.row.pending_units_count }})
          </el-button>
          <div class="action-row">
            <el-button
              v-permission="['manage offer']"
              size="mini"
              icon="el-icon-s-home"
              @click="openUnits(scope.row)"
            >
              Lokale ({{ scope.row.units_count }})
            </el-button>
            <el-button
              v-permission="['manage offer']"
              type="primary"
              size="mini"
              icon="el-icon-edit"
              @click="openEdit(scope.row)"
            >
              Edytuj
            </el-button>
            <el-button
              v-permission="['manage offer']"
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="openDelete(scope.row)"
            >
              Usuń
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <pagination v-show="total > 0" :total="total" :page.sync="query.page" :limit.sync="query.limit" @pagination="getList" />

    <investment-edit-dialog
      :visible.sync="editDialogVisible"
      :investment-id="selected ? selected.id : null"
      @saved="getList"
    />

    <investment-units-dialog
      :visible.sync="unitsDialogVisible"
      :investment-id="selected ? selected.id : null"
      :investment-name="selected ? selected.name : ''"
      @units-changed="getList"
    />

    <!-- Usuwanie inwestycji: los lokali wybiera moderator, domyślnie bezpieczniej (odpięcie). -->
    <el-dialog
      title="Usuwanie inwestycji"
      :visible.sync="deleteDialogVisible"
      width="620px"
      :close-on-click-modal="false"
    >
      <template v-if="selected">
        <p class="delete-lead">
          Usuwasz inwestycję <strong>{{ selected.name }}</strong>.
        </p>

        <template v-if="selected.units_count > 0">
          <p class="delete-lead">
            Do inwestycji przypisanych jest <strong>{{ selected.units_count }}</strong>
            {{ unitsWord(selected.units_count) }} (ogłoszeń). Zdecyduj, co ma się z nimi stać:
          </p>
          <el-radio-group v-model="deleteUnitsMode" class="delete-options">
            <el-radio label="detach">
              Odepnij lokale - zostaną jako zwykłe ogłoszenia
              <div class="sub-text">
                Ogłoszenia pozostają u właściciela. Uwaga: lokale bez własnych zdjęć
                dziedziczyły galerię z inwestycji - po odpięciu zostaną bez zdjęć.
              </div>
            </el-radio>
            <el-radio label="delete">
              Usuń lokale razem z inwestycją
              <div class="sub-text">Skasuje {{ selected.units_count }} {{ unitsWord(selected.units_count) }} wraz ze zdjęciami.</div>
            </el-radio>
          </el-radio-group>
        </template>
        <p v-else class="delete-lead">
          Inwestycja nie ma przypisanych lokali.
        </p>

        <el-alert
          title="Operacji nie da się cofnąć."
          type="warning"
          :closable="false"
          show-icon
        />
      </template>

      <span slot="footer">
        <el-button @click="deleteDialogVisible = false">Anuluj</el-button>
        <el-button type="danger" :loading="deleting" @click="confirmDelete">
          Usuń inwestycję
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import InvestmentResource from '@/api/investment';
import InvestmentEditDialog from '@/views/investments/components/InvestmentEditDialog';
import InvestmentUnitsDialog from '@/views/investments/components/InvestmentUnitsDialog';
import waves from '@/directive/waves';
import permission from '@/directive/permission';

const investmentResource = new InvestmentResource();

/**
 * Moderacja inwestycji deweloperskich: zmiana statusu inwestycji (jak przy
 * ofertach) + hurtowa aktywacja lokali czekających na moderację. Lokale to
 * zwykłe oferty — widać je też w "Lista ofert", ale stąd aktywuje się je
 * jednym klikiem per inwestycja. Aktywacja inwestycji z lokalami w kolejce
 * proponuje od razu ich aktywację (confirm).
 *
 * Dodatkowo edycja danych podstawowych inwestycji (dialog), podgląd lokali z
 * usuwaniem/edycją pojedynczego lokalu oraz usunięcie inwestycji z wyborem losu
 * lokali (odpięcie - domyślnie - albo skasowanie razem z inwestycją).
 */
export default {
  name: 'InvestmentList',
  components: { Pagination, InvestmentEditDialog, InvestmentUnitsDialog },
  directives: { waves, permission },
  data() {
    return {
      list: [],
      total: 0,
      loading: false,
      activatingUnitsId: null,
      selected: null,
      editDialogVisible: false,
      unitsDialogVisible: false,
      deleteDialogVisible: false,
      deleteUnitsMode: 'detach',
      deleting: false,
      query: {
        page: 1,
        limit: 20,
        search: '',
        status: '',
      },
    };
  },
  computed: {
    frontendUrl() {
      return process.env.VUE_APP_FRONTEND_URL;
    },
  },
  created() {
    this.getList();
  },
  methods: {
    formatDate(iso) {
      return iso ? String(iso).slice(0, 10) : '—';
    },
    unitsWord(count) {
      if (count === 1) {
        return 'lokal';
      }
      const rest = count % 10;
      const teens = count % 100;
      if (rest >= 2 && rest <= 4 && (teens < 12 || teens > 14)) {
        return 'lokale';
      }
      return 'lokali';
    },
    openEdit(row) {
      this.selected = row;
      this.editDialogVisible = true;
    },
    openUnits(row) {
      this.selected = row;
      this.unitsDialogVisible = true;
    },
    openDelete(row) {
      this.selected = row;
      // Domyślnie bezpieczniejszy wariant - lokale zostają jako zwykłe ogłoszenia.
      this.deleteUnitsMode = 'detach';
      this.deleteDialogVisible = true;
    },
    async confirmDelete() {
      if (!this.selected) {
        return;
      }
      this.deleting = true;
      try {
        await investmentResource.destroy(this.selected.id, this.deleteUnitsMode === 'delete');
        this.$notify({
          title: 'Sukces',
          message: this.deleteUnitsMode === 'delete'
            ? 'Inwestycja i jej lokale zostały usunięte'
            : 'Inwestycja została usunięta, lokale zostały odpięte',
          type: 'success',
          duration: 3000,
        });
        this.deleteDialogVisible = false;
        this.selected = null;
        this.getList();
      } finally {
        this.deleting = false;
      }
    },
    handleFilter() {
      this.query.page = 1;
      this.getList();
    },
    async getList() {
      this.loading = true;
      try {
        const params = { ...this.query };
        if (!params.status) {
          delete params.status;
        }
        if (!params.search) {
          delete params.search;
        }
        const res = await investmentResource.list(params);
        this.list = res.items;
        this.total = res.total;
      } finally {
        this.loading = false;
      }
    },
    async changeStatus(row, status) {
      try {
        await investmentResource.changeStatus(row.id, status);
        this.$notify({ title: 'Sukces', message: 'Status inwestycji został zmieniony', type: 'success', duration: 2000 });
      } catch (e) {
        this.$notify({ title: 'Błąd', message: 'Nie udało się zmienić statusu', type: 'error', duration: 3000 });
        this.getList();
        return;
      }
      // Aktywacja inwestycji z lokalami w kolejce — zaproponuj od razu ich publikację.
      if (status === 'active' && row.pending_units_count > 0) {
        try {
          await this.$confirm(
            `Inwestycja ma ${row.pending_units_count} lokali czekających na akceptację. Aktywować je teraz?`,
            'Aktywacja lokali',
            { confirmButtonText: 'Aktywuj lokale', cancelButtonText: 'Później', type: 'info' }
          );
        } catch (e) {
          return; // "Później" — lokale zostają do ręcznej akceptacji.
        }
        this.activateUnits(row);
      }
    },
    async activateUnits(row) {
      this.activatingUnitsId = row.id;
      try {
        const res = await investmentResource.activateUnits(row.id);
        row.pending_units_count = 0;
        row.active_units_count = res.active_units_count;
        this.$notify({
          title: 'Sukces',
          message: `Aktywowano ${res.activated} lokali`,
          type: 'success',
          duration: 2000,
        });
      } catch (e) {
        this.$notify({ title: 'Błąd', message: 'Nie udało się aktywować lokali', type: 'error', duration: 3000 });
      } finally {
        this.activatingUnitsId = null;
      }
    },
  },
};
</script>

<style scoped>
.sub-text {
  font-size: 12px;
  color: #909399;
  white-space: normal;
}
.action-row {
  margin-top: 6px;
}
.action-row .el-button + .el-button {
  margin-left: 4px;
}
.delete-lead {
  margin: 0 0 10px;
  line-height: 1.5;
}
.delete-options {
  display: block;
  margin-bottom: 14px;
}
.delete-options .el-radio {
  display: block;
  margin: 0 0 10px;
  white-space: normal;
}
</style>
